import { ActionIcon, Button, Radio, ScrollArea, Table, Tooltip } from '@mantine/core';
import {
  IconCalendar,
  IconCircleCheckFilled,
  IconClipboardTextFilled,
  IconClock,
  IconCloudFilled,
  IconCloudUpload,
  IconCurrentLocationFilled,
  IconEngineFilled,
  IconFileText,
  IconGasStationFilled,
  IconGaugeFilled,
  IconInfoCircle,
  IconInfoCircleFilled,
  IconPaperclip,
  IconPlus,
  IconSettingsFilled,
  IconSteeringWheelFilled,
  IconTrash,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useRef } from 'react';
import { useController, useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatHours } from '../../format.js';
import { DateTimeLtUtcInput, TzOffsetSelect } from '../../inputs/DateTimeLtUtcInput.js';
import { offsetChoices } from '../../inputs/lt-utc.js';
import { ClockLogHint } from './clock-hint.js';
import {
  ComputedField,
  InfoField,
  NoteField,
  NumberField,
  PositionField,
  ScrollTable,
  SelectField,
  TextField,
  useFieldError,
} from './fields.js';
import { FuelLedgerNote, useFuelLedger } from './fuel-ledger.js';
import classes from './NoonReportScreen.module.css';
import { formatPlannedTime, formatTzOffset, type NoonDerived, TZ_OFFSETS } from './noon-report.js';
import { useNumber } from './numbers.js';
import type { Voyage, VoyageReport } from './types.js';

export { useNumber } from './numbers.js';

/** `YYYY-MM-DDTHH:mm` (saat farkıyla yerel) ↔ ISO UTC. */
export function toLocalInput(iso: string, tzOffsetMin: number): string {
  return new Date(Date.parse(iso) + tzOffsetMin * 60_000).toISOString().slice(0, 16);
}
export function fromLocalInput(local: string, tzOffsetMin: number): string | null {
  const at = Date.parse(`${local}:00.000Z`);
  return Number.isNaN(at) ? null : new Date(at - tzOffsetMin * 60_000).toISOString();
}
export function formatStamp(iso: string | null, tzOffsetMin = 0): string {
  if (!iso) return '—';
  const local = toLocalInput(iso, tzOffsetMin);
  return `${local.slice(8, 10)}.${local.slice(5, 7)}.${local.slice(0, 4)} ${local.slice(11, 16)}`;
}

/** Sefer kartlarının ikon boyu ve rengi: lacivert, dolu, 22 px (sefer-noon-report.jpeg; G4-08). */
export const SECTION_ICON = 22;

/**
 * Bölüm başlığı: lacivert dolu ikon, 16 px yarı kalın başlık, altında kenardan kenara çizgi
 * (sefer-noon-report.jpeg "Navigation"). İkon isteğe bağlı: referansta ikonsuz başlıklarda yok.
 */
export function SectionTitle({
  icon,
  title,
  aside,
  divider = true,
}: {
  icon?: ReactNode;
  title: string;
  aside?: ReactNode;
  divider?: boolean;
}) {
  return (
    <div className={classes.sectionTitle} data-divider={divider || undefined}>
      {icon ? (
        <span className={classes.sectionIcon} aria-hidden>
          {icon}
        </span>
      ) : null}
      <h2>{title}</h2>
      {aside ? <span className={classes.sectionAside}>{aside}</span> : null}
    </div>
  );
}

/** Alt kutu: açık mavi başlık şeridi ve ince kenarlık ("Position & Course" …; G4-10). */
export function Group({
  title,
  icon,
  className,
  children,
}: {
  title: string;
  icon?: ReactNode;
  className?: string | undefined;
  children: ReactNode;
}) {
  return (
    <fieldset className={[classes.group, className].filter(Boolean).join(' ')}>
      <legend className={classes.groupTitle}>
        {icon ? (
          <span className={classes.groupIcon} aria-hidden>
            {icon}
          </span>
        ) : null}
        {title}
      </legend>
      <div className={classes.groupBody}>{children}</div>
    </fieldset>
  );
}

/* ---------- 1. Voyage ---------- */

export function VoyageTab({ voyage, legStart }: { voyage: Voyage; legStart: string | null }) {
  const { t } = useTranslation();
  const n = useNumber();
  const rows: [string, string][] = [
    [t('voyage.noon.header.voyageNo'), voyage.voyageNo],
    [t('voyage.noon.header.from'), voyage.fromPort],
    [t('voyage.noon.header.to'), voyage.toPort],
    [t('voyage.noon.voyageTab.charterParty'), voyage.charterPartyNo ?? '—'],
    [t('voyage.noon.voyageTab.charterer'), voyage.charterer ?? '—'],
    [t('voyage.noon.voyageTab.plannedDistance'), n.unit(voyage.plannedDistanceNm, 'NM', 0)],
    [
      t('voyage.noon.voyageTab.plannedEtd'),
      formatPlannedTime(voyage.plannedEtd, voyage.plannedEtdTzOffsetMin),
    ],
    [
      t('voyage.noon.voyageTab.plannedEta'),
      formatPlannedTime(voyage.plannedEta, voyage.plannedEtaTzOffsetMin),
    ],
    [t('voyage.noon.voyageTab.started'), legStart ? formatStamp(legStart) : '—'],
    [
      t('voyage.noon.voyageTab.cpSpeed'),
      `${n(voyage.cpSpeedLadenKn, { digits: 1, fixed: true })} / ${n.unit(voyage.cpSpeedBallastKn, 'kn', { digits: 1, fixed: true })}`,
    ],
    [
      t('voyage.noon.voyageTab.cpConsumption'),
      `${n(voyage.cpConsumptionLadenMt, { digits: 1, fixed: true })} / ${n.unit(voyage.cpConsumptionBallastMt, 'MT/day', { digits: 1, fixed: true })}`,
    ],
    [
      t('voyage.noon.voyageTab.cpWeather'),
      voyage.cpMaxBeaufort === null
        ? '—'
        : t('voyage.noon.voyageTab.beaufortMax', {
            value: voyage.cpMaxBeaufort,
            height: n(voyage.cpMaxWaveHeightM, { digits: 1, fixed: true }),
          }),
    ],
    [
      t('voyage.noon.voyageTab.fuel'),
      `${voyage.mainEngineFuel ?? '—'} / ${voyage.auxEngineFuel ?? '—'}`,
    ],
  ];
  return (
    <section className={classes.card} aria-label={t('voyage.noon.voyageTab.title')}>
      <SectionTitle
        icon={<IconSteeringWheelFilled size={SECTION_ICON} />}
        title={t('voyage.noon.voyageTab.title')}
      />
      <dl className={classes.facts}>
        {rows.map(([label, value]) => (
          <div key={label} className={classes.fact}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/* ---------- 2. Navigation ---------- */

/** Saat farkı listesi iki sütunda, sütun öncelikli (−12:00 … +01:00 | +01:30 … +14:00; G4-16). */
/** Saat farkı listesinde satır yüksekliği (px); kaydırma alanı bunun tam katı (G4-16). */
const OFFSET_ROW_HEIGHT = 22;

function TimeZoneGroup({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation();
  const { field } = useController({ name: 'tzOffsetMin' });
  const reportedAt = useWatch({ name: 'reportedAt' }) as string | undefined;
  const viewport = useRef<HTMLDivElement>(null);
  // Adıma uymayan mevcut fark (saat kaydından +02:40 gibi) listede sırasına eklenir (ADR 0065 §8)
  const options = offsetChoices(Number(field.value), TZ_OFFSETS).map((m) => ({
    value: String(m),
    label: formatTzOffset(m),
  }));
  const rows = Math.ceil(options.length / 2);
  const value = String(field.value);
  // Seçili saat farkı açılışta listenin ortasında görünür; sayfa kaymaz (yalnız liste)
  useEffect(() => {
    const box = viewport.current;
    const checked = box?.querySelector<HTMLElement>(`[data-offset="${value}"]`);
    if (!box || !checked) return;
    const top = checked.getBoundingClientRect().top - box.getBoundingClientRect().top;
    box.scrollTop += top - box.clientHeight / 2 + checked.offsetHeight / 2;
  }, [value]);
  return (
    <Group title={t('voyage.noon.navigation.timeZone')} className={classes.areaTz}>
      {/* UTC farkı ya da IANA bölge adıyla arama (ADR 0057 §2) */}
      <TzOffsetSelect
        label={t('voyage.noon.navigation.utcOffset')}
        required
        value={Number(field.value ?? 0)}
        at={reportedAt ?? null}
        readOnly={readOnly}
        onChange={(next) => field.onChange(next)}
        classNames={{
          input: [classes.input, readOnly ? classes.locked : ''].join(' '),
          label: classes.label ?? '',
          required: classes.required ?? '',
        }}
      />
      {/* Salt okunurken ipucu ve liste gösterilmez: seçim yukarıda (G4-12) */}
      {readOnly ? null : (
        <>
          <p className={classes.hint}>{t('voyage.noon.navigation.utcNote')}</p>
          <ClockLogHint
            reportedAt={reportedAt}
            offset={Number(field.value ?? 0)}
            className={classes.hint}
          />
          <ScrollArea
            h={OFFSET_ROW_HEIGHT * 9}
            type="hover"
            scrollbarSize={6}
            viewportRef={viewport}
            className={classes.offsetScroll}
          >
            <Radio.Group
              value={value}
              onChange={(next) => field.onChange(Number(next))}
              aria-label={t('voyage.noon.navigation.utcOffset')}
            >
              <div
                className={classes.offsets}
                style={{ gridTemplateRows: `repeat(${rows}, ${OFFSET_ROW_HEIGHT}px)` }}
              >
                {options.map((option) => (
                  <Radio
                    key={option.value}
                    size="xs"
                    value={option.value}
                    label={option.label}
                    data-offset={option.value}
                    classNames={{ label: classes.offsetLabel, root: classes.offsetRoot }}
                  />
                ))}
              </div>
            </Radio.Group>
          </ScrollArea>
        </>
      )}
    </Group>
  );
}

/**
 * Tarih-saat alanı: `GG.AA.YYYY SS:DD` (24 saat), değer UTC ISO. Alan başına LT ya da UTC ile
 * girilir, öteki saat yanında görünür (ADR 0057 §2; `DateTimeLtUtcInput`). Etikete giriş biçimi
 * eklenir: "Let Go / Cast Off (LT)". İşletim sisteminin dil biçiminden bağımsız (G4-17, G4-52).
 */
export function LocalDateTimeField({
  name,
  label,
  required,
  readOnly,
  description,
}: {
  name: string;
  label: string;
  required?: boolean;
  readOnly: boolean;
  description?: ReactNode;
}) {
  const { field } = useController({ name });
  const offset = useWatch({ name: 'tzOffsetMin' }) as number;
  const error = useFieldError(name);
  const value = typeof field.value === 'string' && field.value ? field.value : null;
  return (
    <DateTimeLtUtcInput
      // İki saat yan yana sığsın: alan çiftinde tam satır (ADR 0057 §2)
      className={classes.fullRow}
      label={label}
      required={!!required}
      readOnly={readOnly}
      tzOffsetMin={offset ?? 0}
      leftSection={<IconClock size={16} aria-hidden />}
      rightSection={readOnly ? null : <IconCalendar size={16} aria-hidden />}
      value={value}
      onChange={(next) => field.onChange(next)}
      onBlur={field.onBlur}
      error={error ?? null}
      {...(description ? { description } : {})}
      classNames={{
        input: [classes.input, readOnly ? classes.locked : ''].join(' '),
        label: classes.label ?? '',
        required: classes.required ?? '',
        description: classes.fieldNote ?? '',
        placeholder: classes.placeholder ?? '',
      }}
    />
  );
}

export function NavigationForm({
  readOnly,
  derived,
  previous,
}: {
  readOnly: boolean;
  derived: NoonDerived;
  previous: VoyageReport | null;
}) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.noon.navigation.${key}`, options ?? {});
  const offset = (useWatch({ name: 'tzOffsetMin' }) as number | undefined) ?? 0;
  return (
    <section className={classes.card} aria-label={k('title')}>
      <SectionTitle icon={<IconCurrentLocationFilled size={SECTION_ICON} />} title={k('title')} />
      <div className={classes.navGrid}>
        <Group title={k('position')} className={classes.areaPos}>
          <div className={classes.pair}>
            <PositionField
              name="lat"
              axis="lat"
              className={classes.fullRow}
              label={k('latitude')}
              required
              readOnly={readOnly}
            />
            <PositionField
              name="lon"
              axis="lon"
              className={classes.fullRow}
              label={k('longitude')}
              required
              readOnly={readOnly}
            />
            <NumberField
              name="data.navigation.course"
              label={k('course')}
              required
              readOnly={readOnly}
              decimals={1}
            />
            <NumberField
              name="data.navigation.heading"
              label={k('heading')}
              required
              readOnly={readOnly}
              decimals={1}
            />
            <NumberField
              name="data.navigation.sog"
              label={k('sog')}
              readOnly={readOnly}
              required
              decimals={1}
            />
            <NumberField
              name="data.navigation.stw"
              label={k('stw')}
              readOnly={readOnly}
              required
              decimals={1}
            />
          </div>
        </Group>
        <Group title={k('distances')} className={classes.areaDist}>
          <div className={classes.pair}>
            <NumberField
              name="data.navigation.distanceSinceLast"
              label={k('distanceSinceLast')}
              required
              readOnly={readOnly}
              decimals={1}
            />
            <NumberField
              name="data.navigation.distanceThroughWater"
              label={k('distanceThroughWater')}
              required
              readOnly={readOnly}
              decimals={1}
            />
            <NumberField
              name="data.navigation.distanceOverGround"
              label={k('distanceOverGround')}
              required
              readOnly={readOnly}
              decimals={1}
            />
            <NumberField
              name="data.navigation.distanceToGo"
              label={k('distanceToGo')}
              required
              readOnly={readOnly}
              decimals={1}
            />
            <ComputedField
              label={k('averageSpeed')}
              value={n(derived.averageSpeedKn, { digits: 1, fixed: true })}
              required={!readOnly}
            />
            <TextField
              name="data.navigation.nextWaypoint"
              label={k('nextWaypoint')}
              readOnly={readOnly}
            />
          </div>
        </Group>
        <Group title={k('steaming')} className={classes.areaSteam}>
          <InfoField
            label={k('previousTime')}
            value={previous ? formatStamp(previous.reportedAt, offset) : k('noPrevious')}
            leftSection={<IconClock size={16} aria-hidden />}
            rightSection={<IconCalendar size={16} aria-hidden />}
          />
          <LocalDateTimeField name="reportedAt" label={k('currentTime')} readOnly={readOnly} />
          <ComputedField label={k('steamingTime')} value={formatHours(derived.steamingHours)} />
          <p className={classes.hint}>
            <IconInfoCircleFilled size={16} aria-hidden className={classes.hintIcon} />
            {k('steamingNote')}
          </p>
        </Group>
        <TimeZoneGroup readOnly={readOnly} />
        <Group
          title={k('remarks')}
          icon={<IconCurrentLocationFilled size={18} />}
          className={classes.areaRemarks}
        >
          <NoteField
            name="data.navigation.remarks"
            label={k('remarks')}
            readOnly={readOnly}
            placeholder={k('remarksPlaceholder')}
            minRows={2}
          />
        </Group>
      </div>
    </section>
  );
}

/* ---------- 3. Machinery ---------- */

export const MACHINERY_STATUS = ['running', 'standby', 'stopped', 'not_in_use'] as const;

function AuxiliaryTable({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({ name: 'data.machinery.auxiliaries' });
  const k = (key: string) => t(`voyage.noon.machinery.${key}`);
  const statusOptions = MACHINERY_STATUS.map((value) => ({
    value,
    label: t(`voyage.noon.machineryStatus.${value}`),
  }));
  return (
    <>
      <ScrollTable label={k('auxiliaryTable')}>
        <Table className={[classes.table, classes.auxTable].join(' ')} data-cells withColumnBorders>
          <colgroup>
            <col className={classes.colName} />
            <col className={classes.colNum} />
            <col className={classes.colNum} />
            <col className={classes.colNum} />
            <col className={classes.colNum} />
            <col className={classes.colStatus} />
            <col />
            {readOnly ? null : <col className={classes.colAction} />}
          </colgroup>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{k('equipment')}</Table.Th>
              <Table.Th data-numeric>{k('rpm')}</Table.Th>
              <Table.Th data-numeric>{k('load')}</Table.Th>
              <Table.Th data-numeric>{k('runningHoursShort')}</Table.Th>
              <Table.Th data-numeric>{k('hoursSinceLastShort')}</Table.Th>
              <Table.Th>{k('status')}</Table.Th>
              <Table.Th>{k('remarks')}</Table.Th>
              {readOnly ? null : (
                <Table.Th>
                  <span className={classes.srOnly}>{k('removeRow')}</span>
                </Table.Th>
              )}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {fields.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={readOnly ? 7 : 8} className={classes.muted}>
                  {k('noAuxiliary')}
                </Table.Td>
              </Table.Tr>
            ) : null}
            {fields.map((row, index) => {
              const base = `data.machinery.auxiliaries.${index}`;
              const label = (key: string) => `${k(key)} ${index + 1}`;
              return (
                <Table.Tr key={row.id} className={classes.editRow}>
                  <Table.Td>
                    <TextField
                      name={`${base}.name`}
                      label={k('equipment')}
                      ariaLabel={label('equipment')}
                      layout="cell"
                      readOnly={readOnly}
                    />
                  </Table.Td>
                  <Table.Td data-numeric>
                    <NumberField
                      name={`${base}.rpm`}
                      label={k('rpm')}
                      ariaLabel={label('rpm')}
                      layout="cell"
                      readOnly={readOnly}
                      decimals={0}
                    />
                  </Table.Td>
                  <Table.Td data-numeric>
                    <NumberField
                      name={`${base}.loadPct`}
                      label={k('load')}
                      ariaLabel={label('load')}
                      layout="cell"
                      readOnly={readOnly}
                      decimals={0}
                    />
                  </Table.Td>
                  <Table.Td data-numeric>
                    <NumberField
                      name={`${base}.runningHours`}
                      label={k('runningHours')}
                      ariaLabel={label('runningHours')}
                      layout="cell"
                      readOnly={readOnly}
                      decimals={1}
                    />
                  </Table.Td>
                  <Table.Td data-numeric>
                    <NumberField
                      name={`${base}.hoursSinceLast`}
                      label={k('hoursSinceLast')}
                      ariaLabel={label('hoursSinceLast')}
                      layout="cell"
                      readOnly={readOnly}
                      decimals={1}
                    />
                  </Table.Td>
                  <Table.Td className={classes.statusCell}>
                    <SelectField
                      name={`${base}.status`}
                      label={k('status')}
                      ariaLabel={label('status')}
                      layout="cell"
                      readOnly={readOnly}
                      options={statusOptions}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextField
                      name={`${base}.remarks`}
                      label={k('remarks')}
                      ariaLabel={label('remarks')}
                      layout="cell"
                      readOnly={readOnly}
                    />
                  </Table.Td>
                  {readOnly ? null : (
                    <Table.Td className={classes.actionCell}>
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        aria-label={label('removeRow')}
                        className={classes.rowAction}
                        onClick={() => remove(index)}
                      >
                        <IconTrash size={15} />
                      </ActionIcon>
                    </Table.Td>
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollTable>
      {readOnly ? null : (
        <Button
          variant="subtle"
          size="xs"
          className={classes.addButton}
          leftSection={<IconPlus size={14} />}
          onClick={() => append({ name: '', rpm: null, loadPct: null, status: null })}
        >
          {k('addAuxiliary')}
        </Button>
      )}
    </>
  );
}

/** Kaymanın olağan aralığı (sefer-noon-report-makine.jpeg: "Typical range: 5 – 15%"). */
export const SLIP_RANGE = { from: 5, to: 15 } as const;

/** Kayma olağan aralıkta mı; hesaplanamadıysa `null`. */
export const slipNormal = (slip: number | null) =>
  slip === null ? null : slip >= SLIP_RANGE.from && slip <= SLIP_RANGE.to;

export function MachineryForm({ readOnly, derived }: { readOnly: boolean; derived: NoonDerived }) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string) => t(`voyage.noon.machinery.${key}`);
  const stw = useWatch({ name: 'data.navigation.stw' }) as number | null;
  const slip = derived.slipPct;
  const normal = slipNormal(slip);
  const tone = normal === null ? 'neutral' : normal ? 'success' : 'warning';
  const statusOptions = MACHINERY_STATUS.map((value) => ({
    value,
    label: t(`voyage.noon.machineryStatus.${value}`),
  }));
  const speed = { digits: 2, fixed: true } as const;
  return (
    <>
      <div className={classes.machineryTop}>
        <section className={classes.card} aria-label={k('performance')}>
          <SectionTitle icon={<IconEngineFilled size={SECTION_ICON} />} title={k('performance')} />
          <div className={classes.engineGrid}>
            <Group title={k('mainEngine')}>
              <NumberField
                name="data.machinery.mainEngine.rpm"
                label={k('engineSpeed')}
                required
                readOnly={readOnly}
                unit="rpm"
                decimals={0}
              />
              <NumberField
                name="data.machinery.mainEngine.loadPct"
                label={k('engineLoad')}
                required
                readOnly={readOnly}
                unit="%"
                decimals={0}
              />
              <NumberField
                name="data.machinery.mainEngine.shaftRpm"
                label={k('shaftSpeed')}
                required
                readOnly={readOnly}
                unit="rpm"
                decimals={0}
              />
              <div className={classes.pair}>
                <NumberField
                  name="data.machinery.mainEngine.runningHours"
                  label={k('runningHours')}
                  readOnly={readOnly}
                  unit="h"
                  decimals={1}
                />
                {/* Son rapordan beri (ADR 0059 §5); PMS önerisi toplamı okur */}
                <NumberField
                  name="data.machinery.mainEngine.hoursSinceLast"
                  label={k('hoursSinceLast')}
                  readOnly={readOnly}
                  unit="h"
                  decimals={1}
                />
                <NumberField
                  name="data.machinery.mainEngine.powerKw"
                  label={k('power')}
                  readOnly={readOnly}
                  unit="kW"
                  decimals={0}
                />
              </div>
              <SelectField
                name="data.machinery.mainEngine.status"
                label={k('status')}
                readOnly={readOnly}
                options={statusOptions}
              />
            </Group>
            <Group title={k('propeller')}>
              <SelectField
                name="data.machinery.propeller.type"
                label={k('propellerType')}
                readOnly={readOnly}
                options={['fixed', 'controllable'].map((value) => ({
                  value,
                  label: t(`voyage.noon.propellerType.${value}`),
                }))}
              />
              <NumberField
                name="data.machinery.propeller.pitchM"
                label={k('pitch')}
                required
                readOnly={readOnly}
                unit="m"
                fixed
              />
              <NumberField
                name="data.machinery.propeller.diameterM"
                label={k('diameter')}
                readOnly={readOnly}
                unit="m"
                fixed
              />
            </Group>
            <Group title={k('speed')}>
              <ComputedField
                layout="inline"
                label={k('theoreticalSpeed')}
                value={n(derived.theoreticalSpeedKn, speed)}
                rightSection={
                  <Tooltip label={k('theoreticalFormula')} withArrow multiline w={220}>
                    <span
                      className={classes.infoDot}
                      role="img"
                      aria-label={k('theoreticalFormula')}
                    >
                      <IconInfoCircle size={16} aria-hidden />
                    </span>
                  </Tooltip>
                }
              />
              <NumberField
                layout="inline"
                name="data.navigation.stw"
                label={t('voyage.noon.machinery.stw')}
                required
                readOnly={readOnly}
                fixed
              />
              <div className={classes.currentRow}>
                <NumberField
                  layout="inline"
                  name="data.weather.current.speedKn"
                  label={k('current')}
                  readOnly={readOnly}
                  fixed
                />
                <SelectField
                  name="data.weather.current.relation"
                  label={t('voyage.noon.weather.relation')}
                  hideLabel
                  readOnly={readOnly}
                  options={['head', 'following', 'beam'].map((value) => ({
                    value,
                    label: t(`voyage.noon.relation.${value}`),
                  }))}
                />
              </div>
              <NumberField
                layout="inline"
                name="data.navigation.sog"
                label={t('voyage.noon.machinery.sog')}
                readOnly={readOnly}
                fixed
              />
              <div className={classes.slipBox} data-tone={tone}>
                <IconGaugeFilled size={28} aria-hidden className={classes.slipIcon} />
                <span>
                  <span className={classes.slipLabel}>{k('slip')}</span>
                  <strong className={classes.slipValue}>{n.unit(slip, '%', speed)}</strong>
                </span>
              </div>
            </Group>
          </div>
        </section>
        <section className={classes.card} aria-label={k('slipDetails')}>
          <SectionTitle
            icon={<IconInfoCircleFilled size={SECTION_ICON} />}
            title={k('slipDetails')}
          />
          <p className={classes.formula}>
            <span>{`${k('slipShort')} =`}</span>
            <span>{k('slipFormula')}</span>
          </p>
          <dl className={classes.facts}>
            <div className={classes.fact}>
              <dt>{k('theoreticalSpeedShort')}</dt>
              <dd>{n.unit(derived.theoreticalSpeedKn, 'kn', speed)}</dd>
            </div>
            <div className={classes.fact}>
              <dt>STW</dt>
              <dd>{n.unit(stw, 'kn', speed)}</dd>
            </div>
            <div className={classes.fact}>
              <dt>{k('difference')}</dt>
              <dd>
                {derived.theoreticalSpeedKn !== null && stw !== null
                  ? n.unit(derived.theoreticalSpeedKn - stw, 'kn', speed)
                  : '—'}
              </dd>
            </div>
            <div className={classes.fact} data-total>
              <dt>{k('slipShort')}</dt>
              <dd>{n.unit(slip, '%', speed)}</dd>
            </div>
            {/* İkinci slip (SOG) ve akıntı etkisi (ADR 0059 §7) */}
            <div className={classes.fact}>
              <dt>{k('slipSog')}</dt>
              <dd>{n.unit(derived.slipSogPct, '%', speed)}</dd>
            </div>
            <div className={classes.fact}>
              <dt>{k('currentEffect')}</dt>
              <dd>
                {derived.currentEffectKn === null
                  ? '—'
                  : `${n.unit(derived.currentEffectKn, 'kn', speed)} (${n.unit(derived.currentEffectPct, '%', speed)})`}
              </dd>
            </div>
          </dl>
          <p className={classes.hint}>{k('slipSogNote')}</p>
          {normal === null ? null : (
            <div className={classes.notice} data-tone={normal ? 'success' : 'warning'}>
              <IconCircleCheckFilled size={16} aria-hidden />
              <span>
                {normal ? k('slipNormal') : k('slipOut')}
                <br />
                <span className={classes.range}>
                  {t('voyage.noon.machinery.slipRange', SLIP_RANGE)}
                  <Tooltip label={k('slipRangeNote')} withArrow multiline w={220}>
                    <span className={classes.infoDot} role="img" aria-label={k('slipRangeNote')}>
                      <IconInfoCircle size={14} aria-hidden />
                    </span>
                  </Tooltip>
                </span>
              </span>
            </div>
          )}
        </section>
      </div>
      <div className={classes.machineryBottom}>
        <section className={classes.card} aria-label={k('auxiliary')}>
          <SectionTitle icon={<IconSettingsFilled size={SECTION_ICON} />} title={k('auxiliary')} />
          <AuxiliaryTable readOnly={readOnly} />
        </section>
        <FuelConsumptionCard derived={derived} />
        <section className={classes.card} aria-label={k('machineryRemarks')}>
          <SectionTitle
            icon={<IconClipboardTextFilled size={SECTION_ICON} />}
            title={k('machineryRemarks')}
          />
          <NoteField
            name="data.machinery.remarks"
            label={k('machineryRemarks')}
            readOnly={readOnly}
            placeholder={k('remarksPlaceholder')}
          />
        </section>
        <AttachmentsDropCard />
      </div>
    </>
  );
}

type Rec = Record<string, unknown>;
const rec = (value: unknown): Rec =>
  typeof value === 'object' && value !== null ? (value as Rec) : {};
const rows = (value: unknown): Rec[] => (Array.isArray(value) ? (value as Rec[]) : []);
const num = (value: unknown) =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;
const sum = (values: (number | null)[]) =>
  values.some((v) => v !== null)
    ? Math.round(values.reduce<number>((total, v) => total + (v ?? 0), 0) * 1000) / 1000
    : null;

/**
 * Yakıt tüketimi ve ROB (makine sekmesi; G4-28): Fuel & ROB sekmesinin değerleri salt okunur.
 * Yakıt başına tüketim ve kapanış; yağ ROB'u ve toplam (yakıt + yağ). Düzenleme kendi sekmesinde.
 */
function FuelConsumptionCard({ derived }: { derived: NoonDerived }) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string) => t(`voyage.noon.fuelSummary.${key}`);
  const fuel = rec(useWatch({ name: 'data.fuel' }));
  const fuels = rows(fuel.fuels);
  const lubeRob = sum(derived.lubes.map((row) => row.closing));
  const fuelRob = derived.totalRobMt;
  const total = fuelRob === null && lubeRob === null ? null : (fuelRob ?? 0) + (lubeRob ?? 0);
  const mt = { digits: 1, fixed: true } as const;
  return (
    <section className={classes.card} aria-label={k('title')}>
      <SectionTitle icon={<IconGasStationFilled size={SECTION_ICON} />} title={k('title')} />
      <div className={classes.fuelSummary}>
        <ScrollTable label={k('table')}>
          <Table className={classes.table} data-compact withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('voyage.noon.fuel.fuelType')}</Table.Th>
                <Table.Th data-numeric>{k('consumption')}</Table.Th>
                <Table.Th data-numeric>{k('rob')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {fuels.length === 0 ? (
                <Table.Tr>
                  <Table.Td colSpan={3} className={classes.muted}>
                    {t('voyage.noon.fuel.noFuel')}
                  </Table.Td>
                </Table.Tr>
              ) : (
                fuels.map((row, index) => (
                  // biome-ignore lint/suspicious/noArrayIndexKey: salt okunur özet; sıra formdaki gibi
                  <Table.Tr key={`${String(row.fuel)}-${index}`}>
                    <Table.Th scope="row">{String(row.fuel ?? '—')}</Table.Th>
                    <Table.Td data-numeric>{n(num(row.consumption), mt)}</Table.Td>
                    <Table.Td data-numeric>{n(derived.fuels[index]?.closing ?? null, mt)}</Table.Td>
                  </Table.Tr>
                ))
              )}
              {fuels.length > 1 ? (
                <Table.Tr data-total>
                  <Table.Th scope="row">{k('total')}</Table.Th>
                  <Table.Td data-numeric>{n(derived.totalConsumptionMt, mt)}</Table.Td>
                  <Table.Td data-numeric>{n(fuelRob, mt)}</Table.Td>
                </Table.Tr>
              ) : null}
            </Table.Tbody>
          </Table>
        </ScrollTable>
        <div className={classes.robStack}>
          <ComputedField label={k('robFuel')} value={n(fuelRob, mt)} />
          <ComputedField label={k('robLube')} value={n(lubeRob, mt)} />
          <ComputedField label={k('robTotal')} value={n(total, mt)} />
        </div>
      </div>
    </section>
  );
}

/**
 * Ekler (makine sekmesi): sürükle-bırak alanı yer tutucudur; dosya ekleme M11c'nin yükleme
 * altyapısıyla gelir (M11 kabul edilen fark).
 */
function AttachmentsDropCard() {
  const { t } = useTranslation();
  const k = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.noon.attachments.${key}`, options ?? {});
  return (
    <section className={classes.card} aria-label={k('title')}>
      <SectionTitle icon={<IconPaperclip size={SECTION_ICON} stroke={2} />} title={k('title')} />
      <div className={classes.dropRow}>
        <div className={classes.dropZone} aria-disabled="true">
          <IconCloudUpload size={30} aria-hidden />
          <strong>{k('drop')}</strong>
          <small>{k('types')}</small>
          <small className={classes.dropLater}>{k('uploadLater')}</small>
        </div>
        <div className={classes.attachedFiles}>
          <h3>{k('attached', { count: 0 })}</h3>
          <p className={classes.muted}>
            <IconFileText size={16} aria-hidden />
            {k('none')}
          </p>
        </div>
      </div>
    </section>
  );
}

/* ---------- 4. Fuel & ROB ---------- */

const FUELS = ['HFO', 'VLSFO', 'ULSFO', 'MDO', 'MGO', 'LSMGO', 'LNG'] as const;

export function FuelForm({ readOnly, derived }: { readOnly: boolean; derived: NoonDerived }) {
  const { t } = useTranslation();
  const n = useNumber();
  const k = (key: string) => t(`voyage.noon.fuel.${key}`);
  // Defteri açık gemide yakıt sütunları defterden, gönderimde yazılır (ADR 0117 §2.4)
  const ledger = useFuelLedger();
  const fuelReadOnly = readOnly || ledger.active;
  const fuels = useFieldArray({ name: 'data.fuel.fuels' });
  const lubes = useFieldArray({ name: 'data.fuel.lubes' });
  const water = useWatch({ name: 'data.fuel.freshWater' }) as Record<string, unknown> | null;
  const { setValue, getFieldState, formState } = useFormContext();
  const fuelsError = getFieldState('data.fuel.fuels', formState).error?.message;
  const mt = { digits: 1, fixed: true } as const;
  const cell = (name: string, label: string, locked = readOnly) => (
    <NumberField name={name} label={label} layout="cell" readOnly={locked} decimals={2} />
  );
  const removeCell = (onClick: () => void, label: string, locked = readOnly) =>
    locked ? null : (
      <Table.Td className={classes.actionCell}>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="sm"
          aria-label={label}
          className={classes.rowAction}
          onClick={onClick}
        >
          <IconTrash size={15} />
        </ActionIcon>
      </Table.Td>
    );
  const head = (first: string, locked = readOnly) => (
    <Table.Thead>
      <Table.Tr>
        <Table.Th>{first}</Table.Th>
        <Table.Th data-numeric>{k('opening')}</Table.Th>
        <Table.Th data-numeric>{k('consumption')}</Table.Th>
        <Table.Th data-numeric>{k('bunkering')}</Table.Th>
        <Table.Th data-numeric>{k('closing')}</Table.Th>
        {locked ? null : (
          <Table.Th>
            <span className={classes.srOnly}>{t('voyage.noon.machinery.removeRow')}</span>
          </Table.Th>
        )}
      </Table.Tr>
    </Table.Thead>
  );
  return (
    <section className={classes.card} aria-label={k('title')}>
      <SectionTitle icon={<IconGasStationFilled size={SECTION_ICON} />} title={k('title')} />
      <FuelLedgerNote />
      <ScrollTable label={`${k('title')} (MT)`}>
        <Table className={classes.table} data-cells withColumnBorders>
          {head(k('fuelType'), fuelReadOnly)}
          <Table.Tbody>
            {fuels.fields.map((row, index) => {
              const base = `data.fuel.fuels.${index}`;
              return (
                <Table.Tr key={row.id} className={classes.editRow}>
                  <Table.Td>
                    <SelectField
                      name={`${base}.fuel`}
                      label={k('fuelType')}
                      layout="cell"
                      required
                      readOnly={fuelReadOnly}
                      options={FUELS.map((value) => ({ value, label: value }))}
                    />
                  </Table.Td>
                  <Table.Td data-numeric>
                    {cell(`${base}.opening`, k('opening'), fuelReadOnly)}
                  </Table.Td>
                  <Table.Td data-numeric>
                    {cell(`${base}.consumption`, k('consumption'), fuelReadOnly)}
                  </Table.Td>
                  <Table.Td data-numeric>
                    {cell(`${base}.bunkered`, k('bunkering'), fuelReadOnly)}
                  </Table.Td>
                  <Table.Td data-numeric className={classes.closing}>
                    {n(derived.fuels[index]?.closing ?? null, mt)}
                  </Table.Td>
                  {removeCell(
                    () => fuels.remove(index),
                    t('voyage.noon.machinery.removeRow'),
                    fuelReadOnly,
                  )}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollTable>
      {fuelsError && !ledger.active ? <p className={classes.fieldError}>{k('noFuel')}</p> : null}
      {fuelReadOnly ? null : (
        <Button
          variant="subtle"
          size="xs"
          className={classes.addButton}
          leftSection={<IconPlus size={14} />}
          onClick={() => fuels.append({ fuel: null, opening: 0, consumption: 0, bunkered: 0 })}
        >
          {k('addFuel')}
        </Button>
      )}

      <h3 className={classes.subTitle}>{k('lubes')}</h3>
      <ScrollTable label={k('lubes')}>
        <Table className={classes.table} data-cells withColumnBorders>
          {head(k('name'))}
          <Table.Tbody>
            {lubes.fields.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={readOnly ? 5 : 6} className={classes.muted}>
                  {t('common.noData')}
                </Table.Td>
              </Table.Tr>
            ) : null}
            {lubes.fields.map((row, index) => {
              const base = `data.fuel.lubes.${index}`;
              return (
                <Table.Tr key={row.id} className={classes.editRow}>
                  <Table.Td>
                    <TextField
                      name={`${base}.name`}
                      label={k('name')}
                      layout="cell"
                      readOnly={readOnly}
                    />
                  </Table.Td>
                  <Table.Td data-numeric>{cell(`${base}.opening`, k('opening'))}</Table.Td>
                  <Table.Td data-numeric>{cell(`${base}.consumption`, k('consumption'))}</Table.Td>
                  <Table.Td data-numeric>{cell(`${base}.bunkered`, k('bunkering'))}</Table.Td>
                  <Table.Td data-numeric className={classes.closing}>
                    {n(derived.lubes[index]?.closing ?? null, mt)}
                  </Table.Td>
                  {removeCell(() => lubes.remove(index), t('voyage.noon.machinery.removeRow'))}
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </ScrollTable>
      {readOnly ? null : (
        <Button
          variant="subtle"
          size="xs"
          className={classes.addButton}
          leftSection={<IconPlus size={14} />}
          onClick={() => lubes.append({ name: '', opening: 0, consumption: 0, bunkered: 0 })}
        >
          {k('addLube')}
        </Button>
      )}

      <h3 className={classes.subTitle}>{k('freshWater')}</h3>
      {water ? (
        <div className={classes.waterGrid}>
          <NumberField
            name="data.fuel.freshWater.rob"
            label={k('rob')}
            readOnly={readOnly}
            unit="MT"
            decimals={1}
          />
          <NumberField
            name="data.fuel.freshWater.consumption"
            label={k('consumption')}
            readOnly={readOnly}
            unit="MT"
            decimals={1}
          />
          <NumberField
            name="data.fuel.freshWater.produced"
            label={k('produced')}
            readOnly={readOnly}
            unit="MT"
            decimals={1}
          />
          <NumberField
            name="data.fuel.freshWater.bunkered"
            label={k('bunkered')}
            readOnly={readOnly}
            unit="MT"
            decimals={1}
          />
          <ComputedField label={k('closing')} value={n.unit(derived.freshWaterClosing, 'MT', mt)} />
        </div>
      ) : readOnly ? (
        <p className={classes.muted}>—</p>
      ) : (
        <Button
          variant="subtle"
          size="xs"
          className={classes.addButton}
          leftSection={<IconPlus size={14} />}
          onClick={() =>
            setValue('data.fuel.freshWater', { rob: 0, consumption: 0, produced: 0, bunkered: 0 })
          }
        >
          {k('addFreshWater')}
        </Button>
      )}
    </section>
  );
}

/* ---------- 5. Weather & Sea ---------- */

export function WeatherForm({ readOnly }: { readOnly: boolean }) {
  const { t } = useTranslation();
  const k = (key: string) => t(`voyage.noon.weather.${key}`);
  return (
    <section className={classes.card} aria-label={k('title')}>
      <SectionTitle icon={<IconCloudFilled size={SECTION_ICON} />} title={k('title')} />
      <div className={classes.weatherGrid}>
        <Group title={k('wind')}>
          <NumberField
            name="data.weather.wind.direction"
            label={k('direction')}
            required
            readOnly={readOnly}
            decimals={0}
          />
          <NumberField
            name="data.weather.wind.speedKn"
            label={k('speed')}
            required
            readOnly={readOnly}
            decimals={1}
          />
          <NumberField
            name="data.weather.wind.beaufort"
            label={k('beaufort')}
            required
            readOnly={readOnly}
            decimals={0}
          />
        </Group>
        <Group title={k('sea')}>
          <NumberField
            name="data.weather.sea.state"
            label={k('seaState')}
            required
            readOnly={readOnly}
            decimals={0}
          />
          <NumberField
            name="data.weather.sea.swellDirection"
            label={k('swellDirection')}
            required
            readOnly={readOnly}
            decimals={0}
          />
          <NumberField
            name="data.weather.sea.swellHeightM"
            label={k('swellHeight')}
            required
            readOnly={readOnly}
            decimals={1}
          />
          <NumberField
            name="data.weather.sea.swellPeriodS"
            label={k('swellPeriod')}
            readOnly={readOnly}
            decimals={0}
          />
        </Group>
        <Group title={k('weather')}>
          <TextField name="data.weather.condition" label={k('condition')} readOnly={readOnly} />
          <NumberField
            name="data.weather.visibilityNm"
            label={k('visibility')}
            readOnly={readOnly}
            decimals={1}
          />
          <NumberField
            name="data.weather.airTempC"
            label={k('airTemp')}
            readOnly={readOnly}
            decimals={1}
          />
          <NumberField
            name="data.weather.seaTempC"
            label={k('seaTemp')}
            readOnly={readOnly}
            decimals={1}
          />
          <NumberField
            name="data.weather.pressureHpa"
            label={k('pressure')}
            readOnly={readOnly}
            decimals={0}
          />
        </Group>
        <Group title={k('current')}>
          <NumberField
            name="data.weather.current.direction"
            label={k('direction')}
            readOnly={readOnly}
            decimals={0}
          />
          <NumberField
            name="data.weather.current.speedKn"
            label={k('speed')}
            required
            readOnly={readOnly}
            decimals={1}
          />
          <SelectField
            name="data.weather.current.relation"
            label={k('relation')}
            readOnly={readOnly}
            options={['head', 'following', 'beam'].map((value) => ({
              value,
              label: t(`voyage.noon.relation.${value}`),
            }))}
          />
        </Group>
      </div>
      <Group title={k('remarks')} icon={<IconClipboardTextFilled size={18} />}>
        <NoteField name="data.weather.remarks" label={k('remarks')} readOnly={readOnly} />
      </Group>
    </section>
  );
}

/* ---------- 6. Attachments ---------- */

export function AttachmentsTab() {
  const { t } = useTranslation();
  const k = (key: string) => t(`voyage.noon.attachments.${key}`);
  return (
    <section className={classes.card} aria-label={k('title')}>
      <SectionTitle icon={<IconPaperclip size={SECTION_ICON} stroke={2} />} title={k('title')} />
      <div className={classes.dropZone} aria-disabled="true">
        <IconCloudUpload size={30} aria-hidden />
        <strong>{k('drop')}</strong>
        <small>{k('types')}</small>
        <small className={classes.dropLater}>{k('later')}</small>
      </div>
    </section>
  );
}
